/**
 * Suppliers Page
 *
 * Manage dropshipping suppliers and vendors for the store.
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  Plus,
  Search,
  Truck,
  Package,
  Mail,
  Phone,
  Globe,
  MoreVertical,
  Edit,
  Trash2,
  ExternalLink,
  Building2,
  Zap,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';

interface Store {
  id: string;
  name: string;
  slug: string;
  currency: string;
}

interface OutletContext {
  store: Store;
}

interface Supplier {
  id: string;
  name: string;
  code: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  contact_name: string | null;
  supplier_type: string;
  status: string;
  integration_type: string | null;
  auto_order_enabled: boolean;
  currency: string;
  ships_from_country: string | null;
  total_orders: number;
  successful_orders: number;
  failed_orders: number;
  rating: number | null;
  product_count: number;
  created_at: string;
}

const SUPPLIER_TYPES = [
  { value: 'dropship', label: 'Dropship' },
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'print_on_demand', label: 'Print on Demand' },
  { value: 'custom', label: 'Custom' },
];

const STATUS_COLORS: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  active: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="w-4 h-4" /> },
  inactive: { bg: 'bg-gray-100', text: 'text-gray-800', icon: <XCircle className="w-4 h-4" /> },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <Clock className="w-4 h-4" /> },
  suspended: { bg: 'bg-red-100', text: 'text-red-800', icon: <AlertCircle className="w-4 h-4" /> },
};

export default function SuppliersPage() {
  const { t } = useTranslation('store');
  const { storeId } = useParams<{ storeId: string }>();
  const context = useOutletContext<OutletContext | null>();
  const store = context?.store;

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // Form state for quick add
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '',
    supplier_type: 'dropship',
    integration_type: 'manual',
  });

  const loadSuppliers = useCallback(async () => {
    const id = store?.id || storeId;
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (search) params.set('search', search);

      const response = await api.get(`/api/ecommerce/stores/${id}/suppliers?${params}`);
      const data = await response.json() as { suppliers: Supplier[] };
      setSuppliers(data.suppliers || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [store?.id, storeId, statusFilter, typeFilter, search]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const handleQuickAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = store?.id || storeId;
    if (!id || !formData.name.trim()) return;

    try {
      setSaving(true);
      await api.post(`/api/ecommerce/stores/${id}/suppliers`, formData);
      setShowModal(false);
      setFormData({ name: '', code: '', email: '', supplier_type: 'dropship', integration_type: 'manual' });
      loadSuppliers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create supplier');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (supplierId: string) => {
    const id = store?.id || storeId;
    if (!id) return;

    try {
      await api.delete(`/api/ecommerce/stores/${id}/suppliers/${supplierId}`);
      setDeleteConfirm(null);
      loadSuppliers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete supplier');
    }
  };

  const formatSuccessRate = (successful: number, total: number) => {
    if (total === 0) return 'N/A';
    return `${Math.round((successful / total) * 100)}%`;
  };

  if (loading && suppliers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-7 h-7" />
            {t('suppliers.title', 'Suppliers')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('suppliers.description', 'Manage your dropshipping suppliers and vendors')}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          {t('suppliers.addSupplier', 'Add Supplier')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('suppliers.searchPlaceholder', 'Search suppliers...')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">{t('suppliers.allStatuses', 'All Statuses')}</option>
          <option value="active">{t('suppliers.active', 'Active')}</option>
          <option value="inactive">{t('suppliers.inactive', 'Inactive')}</option>
          <option value="pending">{t('suppliers.pending', 'Pending')}</option>
          <option value="suspended">{t('suppliers.suspended', 'Suspended')}</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">{t('suppliers.allTypes', 'All Types')}</option>
          {SUPPLIER_TYPES.map((type) => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Suppliers List */}
      {suppliers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('suppliers.noSuppliers', 'No suppliers yet')}
          </h3>
          <p className="text-gray-500 mb-4">
            {t('suppliers.noSuppliersDescription', 'Add your first supplier to start dropshipping')}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            {t('suppliers.addSupplier', 'Add Supplier')}
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {suppliers.map((supplier) => {
            const statusConfig = STATUS_COLORS[supplier.status] || STATUS_COLORS.inactive;

            return (
              <div
                key={supplier.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/store/${store?.id || storeId}/suppliers/${supplier.id}`}
                          className="text-lg font-semibold text-gray-900 hover:text-blue-600"
                        >
                          {supplier.name}
                        </Link>
                        {supplier.code && (
                          <span className="text-sm text-gray-500">({supplier.code})</span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                          {statusConfig.icon}
                          {supplier.status}
                        </span>
                        {supplier.auto_order_enabled && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <Zap className="w-3 h-3" />
                            Auto
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span className="capitalize">{supplier.supplier_type.replace('_', ' ')}</span>
                        {supplier.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {supplier.email}
                          </span>
                        )}
                        {supplier.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {supplier.phone}
                          </span>
                        )}
                        {supplier.website && (
                          <a
                            href={supplier.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                          >
                            <Globe className="w-3 h-3" />
                            Website
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {/* Stats */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{supplier.product_count}</div>
                        <div className="text-gray-500">{t('suppliers.products', 'Products')}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{supplier.total_orders}</div>
                        <div className="text-gray-500">{t('suppliers.orders', 'Orders')}</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">
                          {formatSuccessRate(supplier.successful_orders, supplier.total_orders)}
                        </div>
                        <div className="text-gray-500">{t('suppliers.successRate', 'Success')}</div>
                      </div>
                    </div>

                    {/* Actions Menu */}
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenu(openMenu === supplier.id ? null : supplier.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-500" />
                      </button>
                      {openMenu === supplier.id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                          <Link
                            to={`/store/${store?.id || storeId}/suppliers/${supplier.id}`}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Edit className="w-4 h-4" />
                            {t('suppliers.edit', 'Edit Supplier')}
                          </Link>
                          <Link
                            to={`/store/${store?.id || storeId}/suppliers/${supplier.id}/products`}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Package className="w-4 h-4" />
                            {t('suppliers.viewProducts', 'View Products')}
                          </Link>
                          <Link
                            to={`/store/${store?.id || storeId}/suppliers/${supplier.id}/orders`}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Truck className="w-4 h-4" />
                            {t('suppliers.viewOrders', 'View Orders')}
                          </Link>
                          <hr className="my-1" />
                          <button
                            onClick={() => {
                              setOpenMenu(null);
                              setDeleteConfirm(supplier.id);
                            }}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                          >
                            <Trash2 className="w-4 h-4" />
                            {t('suppliers.delete', 'Delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">
              {t('suppliers.addSupplier', 'Add Supplier')}
            </h2>
            <form onSubmit={handleQuickAdd}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.supplierName', 'Supplier Name')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={t('suppliers.namePlaceholder', 'e.g., AliExpress Supplier')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.supplierCode', 'Supplier Code')}
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={t('suppliers.codePlaceholder', 'e.g., SUP001')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.email', 'Email')}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="supplier@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.supplierType', 'Supplier Type')}
                  </label>
                  <select
                    value={formData.supplier_type}
                    onChange={(e) => setFormData({ ...formData, supplier_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {SUPPLIER_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('suppliers.integrationType', 'Integration Type')}
                  </label>
                  <select
                    value={formData.integration_type}
                    onChange={(e) => setFormData({ ...formData, integration_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="manual">{t('suppliers.manual', 'Manual')}</option>
                    <option value="email">{t('suppliers.email', 'Email')}</option>
                    <option value="api">{t('suppliers.api', 'API')}</option>
                    <option value="csv">{t('suppliers.csv', 'CSV Export')}</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.name.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? t('common.saving', 'Saving...') : t('suppliers.addSupplier', 'Add Supplier')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-red-600">
              {t('suppliers.confirmDelete', 'Delete Supplier?')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('suppliers.deleteWarning', 'This will remove the supplier and unlink all associated products. This action cannot be undone.')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {openMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setOpenMenu(null)}
        />
      )}
    </div>
  );
}
